/**
 * HoursColumns — two-column hours grid with a live "Open Now" badge.
 * Highlights today's row and shows current open/closed status.
 *
 * Registered as: hours / hours-columns
 */
import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { SectionComponentProps } from "../../registry.js";
import { logConversionEvent } from "@repo/backend/firebase/analytics";

gsap.registerPlugin(ScrollTrigger);

interface HoursDay {
  day: string;
  hours: string;
}

interface HoursColumnsProps {
  headline?: string;
  hours?: HoursDay[];
  phone?: string;
  address?: string;
}

const DEFAULT_HOURS: HoursDay[] = [
  { day: "Monday", hours: "8:00 AM – 6:00 PM" },
  { day: "Tuesday", hours: "8:00 AM – 6:00 PM" },
  { day: "Wednesday", hours: "8:00 AM – 6:00 PM" },
  { day: "Thursday", hours: "8:00 AM – 6:00 PM" },
  { day: "Friday", hours: "8:00 AM – 6:00 PM" },
  { day: "Saturday", hours: "9:00 AM – 4:00 PM" },
  { day: "Sunday", hours: "Closed" },
];

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function isOpenNow(hours: HoursDay[]): { open: boolean; todayEntry: HoursDay | null } {
  const now = new Date();
  const todayName = DAY_NAMES[now.getDay()];
  const todayEntry = hours.find((h) => h.day.toLowerCase() === todayName?.toLowerCase()) ?? null;
  if (!todayEntry || todayEntry.hours.toLowerCase() === "closed") return { open: false, todayEntry };

  // Try to parse "8:00 AM – 6:00 PM"
  const match = todayEntry.hours.match(/(\d+:\d+\s*[AP]M)\s*[–-]\s*(\d+:\d+\s*[AP]M)/i);
  if (!match || !match[1] || !match[2]) return { open: true, todayEntry };

  const parse = (s: string) => {
    const [time, meridiem] = s.trim().split(/\s+/);
    if (!time || !meridiem) return 0;
    const [h, m] = time.split(":").map(Number);
    let hours = h ?? 0;
    if (meridiem.toUpperCase() === "PM" && hours !== 12) hours += 12;
    if (meridiem.toUpperCase() === "AM" && hours === 12) hours = 0;
    return hours * 60 + (m ?? 0);
  };

  const current = now.getHours() * 60 + now.getMinutes();
  const open = current >= parse(match[1]) && current < parse(match[2]);
  return { open, todayEntry };
}

export function HoursColumns({ props }: SectionComponentProps) {
  const {
    headline = "Hours",
    hours = DEFAULT_HOURS,
    phone,
    address,
  } = props as HoursColumnsProps;

  const sectionRef = useRef<HTMLElement>(null);
  const [status, setStatus] = useState<{ open: boolean; todayEntry: HoursDay | null }>({ open: false, todayEntry: null });

  useEffect(() => {
    setStatus(isOpenNow(hours));
  }, [hours]);

  const todayName = DAY_NAMES[new Date().getDay()];

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        "[data-stagger-child]",
        { opacity: 0, y: 18 },
        {
          opacity: 1, y: 0, duration: 0.5, stagger: 0.06, ease: "power2.out",
          scrollTrigger: { trigger: el, start: "top 82%", toggleActions: "play none none none" },
        },
      );
    }, el);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 px-6 bg-background" id="hours">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground">{headline}</h2>
          <span
            className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide px-3 py-1.5 rounded-full ${
              status.open
                ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
                : "bg-muted text-muted-foreground"
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${status.open ? "bg-green-500" : "bg-muted-foreground"}`} />
            {status.open ? "Open Now" : "Closed"}
          </span>
        </div>

        <div className="grid sm:grid-cols-2 gap-x-12 gap-y-0">
          {hours.map((row, i) => {
            const isToday = row.day.toLowerCase() === todayName?.toLowerCase();
            return (
              <div
                key={i}
                data-stagger-child
                className={`flex justify-between items-center py-3 border-b border-border last:border-0 ${
                  isToday ? "font-semibold" : ""
                }`}
              >
                <span className={`text-sm ${isToday ? "text-foreground" : "text-muted-foreground"}`}>{row.day}</span>
                <span
                  className={`text-sm ${
                    row.hours.toLowerCase() === "closed"
                      ? "text-muted-foreground/60"
                      : isToday
                        ? "text-foreground"
                        : "text-muted-foreground"
                  }`}
                  style={isToday ? { color: "var(--color-primary)" } : undefined}
                >
                  {row.hours}
                </span>
              </div>
            );
          })}
        </div>

        {(phone || address) && (
          <div className="mt-8 pt-6 border-t border-border flex flex-wrap gap-6 text-sm text-muted-foreground">
            {address && <span>{address}</span>}
            {phone && (
              <a href={`tel:${phone}`} onClick={() => logConversionEvent("click_call", { phone })} className="text-primary hover:underline font-medium">
                {phone}
              </a>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
